import React, { useEffect, useState, useRef } from "react";
import * as XLSX from "xlsx";
import styles from "./SpreadsheetViewer.module.css";
import { SpreadsheetViewerProps } from "./SpreadsheetViewer.types";
import { fetchExcelData } from "./SpreadsheetViewer.service";
import { ThreeCircles } from "react-loader-spinner";

/**
 * SpreadsheetViewer Component
 * Displays an Excel (.xlsx) file in read-only table format.
 */
const SpreadsheetViewer: React.FC<SpreadsheetViewerProps> = ({ preview }) => {
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [activeSheet, setActiveSheet] = useState<string>("");
  const [sheetData, setSheetData] = useState<any[][]>([]);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [fillerRows, setFillerRows] = useState<any[][]>([]);
  const maxFillerRowCountRef = useRef(0);

  useEffect(() => {
    let loaderTimeout: NodeJS.Timeout;
    if (loading) loaderTimeout = setTimeout(() => setShowLoader(true), 300);
    else setShowLoader(false);
    return () => clearTimeout(loaderTimeout);
  }, [loading]);

  const loadExcel = async (url: string) => {
    try {
      setLoading(true);
      const arrayBuffer = await fetchExcelData(url);
      const wb = XLSX.read(arrayBuffer, { type: "array" });
      setWorkbook(wb);
      setSheetNames(wb.SheetNames);

      const firstSheet = wb.SheetNames[0];
      setActiveSheet(firstSheet);

      const worksheet = wb.Sheets[firstSheet];
      const data: any = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      setSheetData(data);
    } catch (err) {
      console.error("Error loading Excel file:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (preview?.url) loadExcel(preview.url);
  }, [preview?.url]);

  const handleSheetChange = (sheetName: string) => {
    if (!workbook) return;
    setLoading(true);
    setTimeout(() => {
      const worksheet = workbook.Sheets[sheetName];
      const data: any = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      setActiveSheet(sheetName);
      setSheetData(data);
      setLoading(false);
    }, 150);
  };

  const calculateFillerRows = (data: any[][]) => {
    const rowHeight = 28;
    const containerHeight =
      typeof window !== "undefined" ? window.innerHeight - 275 : 600;

    const visibleRowCount = Math.floor(containerHeight / rowHeight);
    const fillerRowCount = Math.max(0, visibleRowCount - (data?.length || 0));

    const colCount = Math.max(
      data[0]?.length || 0,
      ...data.map((r) => r.length)
    );

    maxFillerRowCountRef.current = Math.max(
      maxFillerRowCountRef.current,
      fillerRowCount
    );

    return Array.from({ length: maxFillerRowCountRef.current }).map(() =>
      Array.from({ length: colCount }).map(() => "")
    );
  };

  useEffect(() => {
    setFillerRows(calculateFillerRows(sheetData));
  }, [sheetData, activeSheet]);

  return (
    <div className={styles.spreadsheetViewerWrapper}>
      {preview?.url ? (
        <>
          <div className={styles.toolbar}>
            <div className={styles.fileTitle}>{preview?.name}</div>
          </div>

          {sheetNames.length > 1 && (
            <div className={styles.sheetTabsRow}>
              {sheetNames.map((name) => (
                <button
                  key={name}
                  className={`${styles.sheetTab} ${name === activeSheet ? styles.activeTab : ""
                    }`}
                  onClick={() => handleSheetChange(name)}
                >
                  {name}
                </button>
              ))}
            </div>
          )}

          {showLoader && (
            <div className={styles.customGridLoader}>
              <ThreeCircles
                height="50"
                width="50"
                color="#4fa94d"
                visible={true}
                ariaLabel="three-circles-rotating"
                outerCircleColor="var(--theme1_tertiary)"
                innerCircleColor="var(--theme1_secondary)"
                middleCircleColor="var(--theme1_primary)"
              />
              <div className={styles.customGridLoaderText}>
                Loading your data
              </div>
            </div>
          )}

          {!loading && (
            <div className={styles.tableContainer}>
              <table className={styles.sheetTable}>
                <tbody>
                  {sheetData.map((row, i) => (
                    <tr key={`data-${activeSheet}-${i}`}>
                      {row.map((cell, j) => (
                        <td key={`cell-${activeSheet}-${i}-${j}`}>{cell}</td>
                      ))}
                    </tr>
                  ))}

                  {fillerRows.map((row, i) => (
                    <tr key={`filler-${activeSheet}-${i}`}>
                      {row.map((_, j) => (
                        <td key={`filler-cell-${activeSheet}-${i}-${j}`}>
                          &nbsp;
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <span className={styles.previewFileMessage}>
          Please select an Excel file to preview.
        </span>
      )}
    </div>
  );
};

export default SpreadsheetViewer;
